import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CommentaryPost, Deck, Match, Pilot } from '@edh/shared';
import {
  PLACEMENT_GAMES,
  RANK_BAND,
  bandProgress,
  columnsOf,
  commanderKey,
  deckLabel,
  display,
  exitOf,
  isRanked,
  isRated,
  lifeSeries,
  placingOf,
  survivalOf,
  winConditionLabel,
} from '@edh/shared';
import { Avatar } from './Avatar';
import { CommentaryRecord } from './Commentary';
import { LifeTimeline } from './LifeTimeline';
import { useDeckArt, usePartnerArt } from '../lib/art';
import { lockScroll } from '../lib/scrollLock';
import { useLookups, useStore, type MatchResult } from '../lib/store';

/**
 * Spec 8 — the memorable moment, and the only screen in the app allowed to take
 * its time.
 *
 * Full screen rather than a sheet: a drawer is a thing you dismiss to get back
 * to what you were doing, and this is not in the way of anything. The game just
 * ended, everyone is looking at the phone, and the ratings that moved are the
 * whole reason the league exists.
 *
 * The beats are staged rather than played at once — art, winner, facts, then
 * each player in turn, then the breakdown. Six things arriving together is a
 * flash; the same six arriving in order is a result being read out.
 */

const ORDINAL = ['—', '1st', '2nd', '3rd', '4th'];

/**
 * What a placing is called on the screen of the person who got it.
 *
 * Only last place is a defeat. Second of four is a good night and reading it as
 * the same word as dying first would be both unkind and wrong — the rating
 * agrees, since second in an even field gains points.
 */
const PLACING = ['—', 'Victory', 'Second', 'Third', 'Fourth'];

/**
 * Your game, out of a record of everybody's.
 *
 * Three quarters of the people who ever see this screen lost, so losing is the
 * normal case and not the sad one. What makes it worth looking at is that it is
 * about you: where you came, how you went out, and — often — that your rating
 * went up anyway.
 *
 * The facts come from the shared derivation; this only says them out loud.
 */
function yourGame(
  match: Match,
  pilotId: string,
  nameOf: (id: string) => string | undefined,
): { headline: string; ending: string | null; won: boolean } {
  // A game nobody won is not a game you lost. Without this the sheet tells
  // every player at a cancelled table "Defeat", because not winning is the
  // only other thing `placingOf` knows how to say.
  if (!isRated(match)) return { headline: 'No result', ending: null, won: false };
  const { place, last, won } = placingOf(match, pilotId);
  const headline = won
    ? 'Victory'
    : last || place === undefined
      ? 'Defeat'
      : (PLACING[place] ?? ORDINAL[place] ?? `${place}`);

  const exit = exitOf(match, pilotId);
  const ending =
    exit.kind === 'won'
      ? null
      : exit.kind === 'poison'
        ? 'Poisoned out'
        : exit.kind === 'commander'
          ? `Killed by ${nameOf(exit.source ?? '') ?? 'a'}'s commander`
          : exit.kind === 'survived'
            ? `Still standing at ${exit.life} life`
            : exit.turn !== undefined
              ? `Out on turn ${exit.turn}`
              : exit.kind === 'life'
                ? 'Out'
                : null;

  return { headline, ending, won };
}

/** Wall clock, in the units people say out loud. */
function clock(seconds: number): string {
  if (seconds < 90) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

/** Beat timings, in ms from mount. Everything on this screen hangs off these. */
const T_FACTS = 900;
const T_ODDS = 1500;
const T_ROWS = 2000;
/** Gap between one player's line and the next. */
const T_STAGGER = 900;
/** How long a rating takes to travel, once its line has arrived. */
const ROLL_MS = 3400;
/**
 * And how long the swing beside it takes to count out.
 *
 * Quicker than the rating: the swing is the headline and the total is the
 * consequence, so it lands first and the rating catches up to it.
 */
const SWING_MS = 2200;

export function ResultSheet({
  result,
  onClose,
  feed,
}: {
  result: MatchResult;
  onClose: () => void;
  /**
   * The live feed, for the minute after a game ends.
   *
   * The closing posts are written just after the match is, and land on a
   * schedule over the following minute - which is exactly while this screen is
   * up. The stored copy on the match is what History reads later; this is what
   * makes them appear here as they arrive rather than only in hindsight.
   */
  feed?: CommentaryPost[];
}) {
  const [skip, setSkip] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const unlock = lockScroll();
    return () => {
      window.removeEventListener('keydown', onKey);
      unlock();
    };
  }, [onClose]);

  return createPortal(
    <div
      className={`result-screen${skip ? ' skip' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="Result"
      /* A tap anywhere brings the whole thing forward. Drawing it out is right
         the first time and wrong the twentieth, and a ceremony you cannot walk
         out of stops being one. */
      onClick={() => setSkip(true)}
    >
      <div className="result-scroll">
        <MatchSummary
          match={result.match}
          revealed={result.revealed}
          skip={skip}
          live
          feed={feed}
        />
        <button className="btn primary block result-done" onClick={onClose}>
          Done
        </button>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Everything the app knows about one game.
 *
 * Shared by the screen that appears when a game ends and the drawer that opens
 * from the history, because they are the same information and the difference is
 * only whether it is arriving or being looked up. `live` is what separates
 * them: a fresh result stages itself and shows ratings as they stand now; a
 * game from the history arrives whole, and shows the ratings as they stood when
 * it was played.
 */
export function MatchSummary({
  match,
  revealed,
  skip = false,
  live = false,
  feed,
}: {
  match: Match;
  revealed?: { pilots: string[]; decks: string[] };
  skip?: boolean;
  live?: boolean;
  /** Posts arriving right now; falls back to whatever the match kept. */
  feed?: CommentaryPost[];
}) {
  const { state, me } = useStore();
  const { pilot, deck } = useLookups();

  const winnerEntry = match.entries.find((e) => e.pilotId === match.winnerPilotId);
  const winnerDeck = winnerEntry ? deck(winnerEntry.deckId) : undefined;
  const winnerArt = useDeckArt(winnerDeck);
  const winner = match.winnerPilotId ? pilot(match.winnerPilotId) : undefined;

  /*
   * Your own deck's art, on your own copy of the result.
   *
   * It was the winner's, cooled — "this is what beat you" — which is a fine
   * thing for the screen to say and not what this screen is for. It is about
   * your game, so it is your commander behind it. The winner is still named on
   * the line underneath.
   */
  const myEntry = me ? match.entries.find((e) => e.pilotId === me.id) : undefined;
  const myDeck = myEntry ? deck(myEntry.deckId) : undefined;
  const myArt = useDeckArt(myDeck);

  // Whichever deck the banner is showing, its second commander comes with it.
  const winnerPartnerArt = usePartnerArt(winnerDeck);
  const myPartnerArt = usePartnerArt(myDeck);

  /**
   * The winner leads; everyone else follows in the order they sat.
   *
   * Same list, one ordering — the person who won is not a separate kind of
   * entry, they are just the one at the top.
   */
  const lines = match.standing
    ? [...match.entries].sort(
        (a, b) => match.standing![a.pilotId]! - match.standing![b.pilotId]!,
      )
    : [
        ...match.entries.filter((e) => e.pilotId === match.winnerPilotId),
        ...match.entries.filter((e) => e.pilotId !== match.winnerPilotId),
      ];

  /**
   * The ratings as they stood when this game finished.
   *
   * A game from three weeks ago has been overtaken by everything since, so
   * showing today's number beside that night's swing would be two halves of
   * different sentences. Rewinding the later deltas puts them back together.
   */
  const later = live ? [] : state.matches.filter((m) => m.ts > match.ts);
  const eloAfter = (id: string, kind: 'decks' | 'pilots', current: number) =>
    later.reduce((elo, m) => elo - (m.deltas[kind][id] ?? 0), current);

  /**
   * How many placement games were behind this deck when this one finished.
   *
   * Same rewind as the rating: a deck that was 2/5 that night is 5/5 now, and
   * showing today's count against that night's swing would say the game was
   * scored under rules it was not.
   */
  const deckPlayedAfter = (deckId: string, current: number) =>
    current - later.filter((m) => m.entries.some((e) => e.deckId === deckId)).length;
  const pilotPlayedAfter = (pilotId: string, current: number) =>
    current - later.filter((m) => m.entries.some((e) => e.pilotId === pilotId)).length;

  /** Only a game played through the counter has anything to break down. */
  const hasCounterData = (match.damageLog?.length ?? 0) > 0 && (match.endTurn ?? 0) > 0;
  const [view, setView] = useState<'summary' | 'players'>('summary');

  const facts: string[] = [];
  if (match.endTurn) facts.push(`Turn ${match.endTurn}`);
  if (match.durationSeconds) facts.push(clock(match.durationSeconds));
  const winnerLife = match.winnerPilotId ? match.lifeRemaining?.[match.winnerPilotId] : undefined;
  if (winnerLife !== undefined) facts.push(`${winnerLife} life`);
  if (match.winCondition) facts.push(winConditionLabel(match.winCondition));
  // "On the play" is the game's own term for going first, and it means nothing
  // to anyone who has not already been told. The tag only appears when the
  // winner started, so it is worth saying plainly: they had the first turn.
  if (match.onThePlay === match.winnerPilotId) facts.push('Went first');

  /*
   * Whose screen this is.
   *
   * Only for somebody who actually played: a leader looking a game up in the
   * history did not lose it, and telling them they did would be nonsense. They
   * get the game's own story, which is the winner's.
   */
  const mine = me && match.entries.some((e) => e.pilotId === me.id) ? me : undefined;
  const yours = mine ? yourGame(match, mine.id, (id) => pilot(id)?.name) : null;
  /*
   * Only the ceremony is personal. A game opened from the history is a record
   * of what happened, and somebody scrolling back to check who won that one on
   * Tuesday does not need to be told they lost it — they knew.
   */
  const personal = Boolean(live && yours && !yours.won);

  /**
   * The best sentence this screen can produce: you lost, and you climbed.
   *
   * True often enough to be worth saying — second in an even four-player field
   * gains about four points, because average finish is two and a half. It is
   * the whole argument for recording a finishing order, and without saying it
   * out loud nobody would ever notice it happening.
   *
   * Sealed during placement like every other view of the rating.
   */
  const myDelta = mine ? (match.deltas.pilots[mine.id] ?? 0) : 0;
  const myRanked = mine ? isRanked(pilotPlayedAfter(mine.id, mine.placementGames)) : false;
  const climbed = personal && myRanked && myDelta > 0;

  // A game being looked up has no ceremony: everything is already true.
  const factsAt = live ? T_FACTS : 0;
  const oddsAt = live ? T_ODDS : 0;
  const rowAt = (i: number) => (live ? T_ROWS + i * T_STAGGER : 0);
  const breakdownAt = live ? T_ROWS + (lines.length - 1) * T_STAGGER + ROLL_MS : 0;

  return (
    <>
        {/*
          Said before anything else on the screen, because everything below it
          is shaped like a result: a commander across the top, a list with
          somebody at the head of it, a row of numbers. All of that is still
          true as a record of what happened at the table, and none of it means
          what it usually means.
        */}
        {!isRated(match) && (
          <div className="cancelled-banner">
            <span className="kicker">Cancelled</span>
            <p>
              This game was played and did not finish. Nobody won it, and it moved nobody&apos;s
              rating.
            </p>
          </div>
        )}
        {/*
          Your commander when it is your result, the winner's when it is the
          game's. Cooled either way on a loss — the one celebratory treatment in
          the app is for the seat that earned it.
        */}
        {(() => {
          const shown = personal && myDeck ? myDeck : winnerDeck;
          const front = personal && myArt ? myArt : winnerArt;
          const second = personal && myArt ? myPartnerArt : winnerPartnerArt;
          const paired = Boolean(shown?.partner && second);
          return (
        <div
          className={`result-art${live ? '' : ' still'}${personal ? ' cooled' : ''}${
            paired ? ' paired' : ''
          }`}
          style={!paired && front ? { backgroundImage: `url(${front})` } : undefined}
        >
          {paired && (
            <>
              <span
                className="hero-half left"
                style={front ? { backgroundImage: `url(${front})` } : undefined}
              />
              <span
                className="hero-half right"
                style={second ? { backgroundImage: `url(${second})` } : undefined}
              />
            </>
          )}
          {/* One pass of light across the art, once, on arrival. The whole app
              is grey and quiet so that this is allowed to be neither — and a
              game being looked up weeks later is not that moment. Victory earns
              it; the other three seats did not. */}
          {live && !personal && <span className="result-flare" />}
          <div className="result-winner">
            <div className="who">{!isRated(match) ? 'No result' : (yours?.headline ?? 'Victory')}</div>
            {/* Nobody to put a face to. The row is dropped rather than filled
                with "Unknown", which is what it would otherwise say. */}
            {isRated(match) && (
              <div className="what">
                <span className="result-face">
                  {personal ? (
                    <Avatar name={mine!.name} src={mine!.avatar} size={52} />
                  ) : (
                    <Avatar name={winner?.name ?? '?'} src={winner?.avatar} size={52} />
                  )}
                </span>
                <span>{personal ? mine!.name : (winner?.name ?? 'Unknown')}</span>
              </div>
            )}
            <div className="with">
              {!isRated(match)
                ? match.note || 'Cancelled'
                : personal
                  ? (yours!.ending ?? 'Out')
                  : winnerDeck
                    ? deckLabel(winnerDeck)
                    : 'Unknown deck'}
            </div>
          </div>
        </div>
          );
        })()}

        {/* Who actually took it. Demoted to a line rather than removed: it is
            still the story of the game, just no longer the headline of your
            copy of it. */}
        {personal && (
          <div className="result-taken" style={{ animationDelay: `${factsAt / 1000}s` }}>
            <Avatar name={winner?.name ?? '?'} src={winner?.avatar} size={22} />
            <span>
              <strong>{winner?.name ?? 'Unknown'}</strong> won
              {winnerDeck ? ` with ${deckLabel(winnerDeck)}` : ''}
            </span>
          </div>
        )}

        {facts.length > 0 && (
          <div className="result-facts">
            {facts.map((fact, i) => (
              <span
                className="result-fact"
                key={fact}
                style={{ animationDelay: `${(factsAt + i * 120) / 1000}s` }}
              >
                {fact}
              </span>
            ))}
          </div>
        )}

        {/* What the table was told beforehand. An upset is only an upset if
            somebody said so first — and naming the model keeps the number
            honest about being a forecast rather than a fact. */}
        {match.winnerPilotId !== null && match.predicted?.[match.winnerPilotId] !== undefined && (
          <div className="result-odds" style={{ animationDelay: `${oddsAt / 1000}s` }}>
            <span className="kicker">Polymarket results</span>
            {(() => {
              const odds = match.predicted!;
              const mine = odds[match.winnerPilotId!]!;
              const n = match.entries.length;
              /*
               * Even money is one over the pod size, not fifty per cent.
               * Judging a four-player game against a coin flip called every
               * winner an upset, which made the word mean nothing.
               */
              const par = 1 / n;
              const seat =
                match.entries.filter((e) => (odds[e.pilotId] ?? 0) > mine).length + 1;
              const verdict =
                seat === 1
                  ? 'the favourite'
                  : seat === n
                    ? 'the longest shot at the table'
                    : `${ORDINAL[seat] ?? `${seat}th`} favourite of ${n}`;

              return (
                <>
                  <span className="odds-headline">
                    <span className="num">{Math.round(mine * 100)}%</span>
                    <span className="dot">·</span>
                    <span className={mine < par ? 'odds-upset' : undefined}>{verdict}</span>
                  </span>
                  <span className="odds-note">
                    What {pilot(match.winnerPilotId!)?.name ?? 'the winner'} was given before a
                    card was drawn, from every rating and bracket at the table. Even money in a{' '}
                    {n}-player game is {Math.round(par * 100)}%.
                  </span>
                </>
              );
            })()}
          </div>
        )}

        {/* Above the ratings rather than buried under them: the two views are
            two answers to "how did that go", and the switch belongs where the
            question is asked. */}
        {hasCounterData && (
          <div
            className="segmented result-switch"
            role="tablist"
            aria-label="Result detail"
            // Last, after every rating has finished moving. Offering a second
            // view of a result still arriving is asking people to leave before
            // the thing they are watching has happened.
            style={{ animationDelay: `${breakdownAt / 1000}s` }}
          >
            <span
              className="segmented-thumb"
              style={{ transform: `translateX(${view === 'players' ? '100%' : '0%'})` }}
            />
            <button
              className="segmented-option"
              role="tab"
              aria-selected={view === 'summary'}
              onClick={() => setView('summary')}
            >
              Summary
            </button>
            <button
              className="segmented-option"
              role="tab"
              aria-selected={view === 'players'}
              onClick={() => setView('players')}
            >
              Stats
            </button>
          </div>
        )}

        {view === 'players' && hasCounterData ? (
          <PlayerTable match={match} />
        ) : (
        <div className="result-list">
          {lines.map((entry, i) => {
            const d = deck(entry.deckId);
            const p = pilot(entry.pilotId);
            return (
              <ResultLine
                key={entry.deckId}
                deck={d}
                pilot={p}
                deckElo={d ? eloAfter(entry.deckId, 'decks', d.elo) : 0}
                deckPlayed={d ? deckPlayedAfter(entry.deckId, d.placementGames) : 0}
                pilotElo={p ? eloAfter(entry.pilotId, 'pilots', p.elo) : 0}
                pilotPlayed={p ? pilotPlayedAfter(entry.pilotId, p.placementGames) : 0}
                deckDelta={match.deltas.decks[entry.deckId] ?? 0}
                pilotDelta={match.deltas.pilots[entry.pilotId] ?? 0}
                mine={entry.pilotId === me?.id}
                place={match.standing?.[entry.pilotId]}
                at={rowAt(i)}
                skip={skip || !live}
              />
            );
          })}
        </div>
        )}

        {climbed && (
          <p className="result-climbed" style={{ animationDelay: `${breakdownAt / 1000}s` }}>
            Your rating went up anyway. Finishing above what the table expected of you
            pays, with or without the win.
          </p>
        )}

        {/* Spec 2.5 — don't let the number quietly appear. */}
        {[
          ...(revealed?.decks ?? []).map((id) => ({ kind: 'deck' as const, id })),
          ...(revealed?.pilots ?? []).map((id) => ({ kind: 'pilot' as const, id })),
        ].map((item, i) => {
          const subject = item.kind === 'deck' ? deck(item.id) : pilot(item.id);
          if (!subject) return null;
          const at = breakdownAt + i * 700;
          return (
            <div className="reveal" key={`${item.kind}-${item.id}`} style={{ animationDelay: `${at / 1000}s` }}>
              <div className="kicker">Placements complete</div>
              <div className="name">{subject.name}</div>
              <div className="rating" style={{ animationDelay: `${(at + 200) / 1000}s` }}>
                {display(subject.elo)}
              </div>
            </div>
          );
        })}

        {view === 'summary' && <Breakdown match={match} at={breakdownAt} />}

        {/* Live while the closing posts are still landing, stored afterwards.
            The live copy wins because it is the one still growing. */}
        <CommentaryRecord posts={feed?.length ? feed : (match.commentary ?? [])} />
    </>
  );
}

/**
 * A number travelling from where it was to where it is.
 *
 * The rating appearing already changed is information; watching it move is the
 * result. Everything else on this screen is in service of the two seconds this
 * takes.
 */
function useRoll(from: number, to: number, run: boolean, duration = ROLL_MS): number {
  const [value, setValue] = useState(from);
  const frame = useRef(0);

  useEffect(() => {
    if (!run) {
      setValue(from);
      return;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setValue(to);
      return;
    }
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      /*
       * Quintic ease-out: most of the distance is covered in the first third
       * and the last few points take the rest of the time. A cubic settled too
       * politely — the number arrived without ever having looked like it was
       * moving fast.
       */
      setValue(from + (to - from) * (1 - Math.pow(1 - t, 5)));
      if (t < 1) frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [from, to, run, duration]);

  return value;
}

/**
 * Where the rating stands, drawn around the player it belongs to.
 *
 * A ring rather than a bar. The bar was measuring a rating's position within the
 * league's whole spread, which is a fine thing for a ladder and the wrong
 * question here. This measures progress through the current hundred points: it
 * fills as the rating climbs and spills over into the next band on the way past,
 * so a game that pushes a deck through 1300 shows the ring wrap rather than a
 * number quietly gaining a digit.
 *
 * It is driven by the same value the number is counting through, which is what
 * makes the wrap land on the exact frame the hundred is crossed.
 */
function EloRing({
  name,
  avatar,
  portion,
  from,
  wrapped,
  up,
  size = 62,
}: {
  name: string;
  avatar?: string | null;
  /** Where the ring stands now, 0 to 1 through the current band. */
  portion: number;
  /** Where it stood before this game. */
  from: number;
  /** True once the roll has crossed into a new hundred. */
  wrapped: boolean;
  up: boolean;
  size?: number;
}) {
  const stroke = 5;
  const r = (size - stroke) / 2;
  const C = 2 * Math.PI * r;
  const now = Math.min(1, Math.max(0, portion));

  /**
   * The part of the ring this game is responsible for, drawn over the rest.
   *
   * It grows out of where the rating stood as the number rolls, so the gain is
   * something you watch happen rather than a state you arrive at. Crossing a
   * hundred restarts it at the top of the new band — which is what spilling
   * over looks like, and why this is recomputed every frame rather than handed
   * to a CSS transition that would have to be told about the wrap.
   */
  const lo = wrapped ? 0 : Math.min(from, now);
  const hi = wrapped ? now : Math.max(from, now);
  const span = Math.max(0, hi - lo);

  const swingColor = up ? '#57a566' : '#d45b47';

  /*
   * One track, two segments, and they never overlap.
   *
   * Gold runs to whichever is smaller — where the rating was, or where it is —
   * so it always stops at ground the rating holds under either reading. The
   * swing takes the difference: on a gain it extends past the gold and the ring
   * plainly grows; on a loss it is the stretch the gold has given up, drawn
   * lighter so the solid gold ending short of it is what the eye follows.
   *
   * Stacking gold to the current value and then colouring the same stretch
   * again was the bug: on a gain the two agreed and it looked doubled, and on a
   * loss the coloured part filled the space the gold vacated so the ring never
   * appeared to move at all.
   */
  return (
    <span className="elo-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--hairline)"
          strokeWidth={stroke}
        />
        {/* Where it was, or where it is — whichever is less. */}
        {lo > 0.001 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="var(--gold)"
            strokeWidth={stroke}
            // Butt, not round. A rounded cap sticks out past the end of its own
            // arc, so the two segments each grew half a stroke into the other
            // and the join came out as a smear of both colours.
            strokeLinecap="butt"
            strokeDasharray={`${lo * C} ${C}`}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        )}
        {/* And what this game did on top of it. */}
        {span > 0.001 && (
          <circle
            className={`elo-ring-swing${up ? '' : ' lost'}`}
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={swingColor}
            // The glow filter reads the colour from here, so it follows the
            // direction without a second constant to keep in step.
            style={{ color: swingColor }}
            strokeWidth={stroke}
            strokeLinecap="butt"
            strokeDasharray={`${span * C} ${C}`}
            strokeDashoffset={-lo * C}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        )}
      </svg>
      <span className="elo-ring-face">
        <Avatar name={name} src={avatar} size={Math.round(r * 2 - stroke - 6)} />
      </span>
    </span>
  );
}

/**
 * One player's line: who, on what, where the rating moved, and where that
 * leaves them.
 *
 * Held back until its turn rather than hidden — everything inside is laid out
 * from the start so nothing below jumps when it arrives, and only the animation
 * waits.
 */
function ResultLine({
  deck,
  pilot,
  deckElo,
  deckPlayed,
  pilotElo,
  pilotPlayed,
  deckDelta,
  pilotDelta,
  mine,
  place,
  at,
  skip,
}: {
  deck: Deck | undefined;
  pilot: Pilot | undefined;
  /** Ratings and placement counts as they stood after this game, not today. */
  deckElo: number;
  deckPlayed: number;
  pilotElo: number;
  pilotPlayed: number;
  deckDelta: number;
  pilotDelta: number;
  /**
   * Whether this line is the person reading it.
   *
   * The highlight used to mark the winner, who is already named in the banner
   * above, ordinalled 1st, and sitting at the top of the list — three ways of
   * saying the same thing and none of them answering the question anyone opens
   * this screen with, which is what happened to me.
   */
  mine: boolean;
  /** Where they finished, when the game recorded one. */
  place?: number;
  at: number;
  skip: boolean;
}) {
  const [shown, setShown] = useState(false);
  const art = useDeckArt(deck);

  useEffect(() => {
    if (skip) {
      setShown(true);
      return;
    }
    const id = window.setTimeout(() => setShown(true), at);
    return () => window.clearTimeout(id);
  }, [at, skip]);

  const rolledDeck = useRoll(deckElo - deckDelta, deckElo, shown);
  const rolledPilot = useRoll(pilotElo - pilotDelta, pilotElo, shown);
  /**
   * The placement count travels too.
   *
   * Its ring was a fraction of a fixed integer, so it arrived at its new length
   * already there — the one player on the screen with nothing to watch. Rolling
   * the count gives the fifth of a ring somewhere to come from.
   */
  const rolledPlayed = useRoll(pilotPlayed - 1, pilotPlayed, shown);
  const swingDeck = useRoll(0, deckDelta, shown, SWING_MS);
  const swingPilot = useRoll(0, pilotDelta, shown, SWING_MS);

  if (!deck) return null;
  const deckRanked = isRanked(deckPlayed);
  const pilotRanked = isRanked(pilotPlayed);

  /**
   * Still placing means no rating worth ringing, so the ring measures the other
   * thing being worked through: games until there is one, with this game's
   * single step highlighted the same way a rating swing would be.
   */
  const startPilotElo = pilotElo - pilotDelta;
  const portion = pilotRanked ? bandProgress(rolledPilot) : rolledPlayed / PLACEMENT_GAMES;
  const ringFrom = pilotRanked
    ? bandProgress(startPilotElo)
    : (pilotPlayed - 1) / PLACEMENT_GAMES;
  const wrapped =
    pilotRanked &&
    Math.floor(rolledPilot / RANK_BAND) !== Math.floor(startPilotElo / RANK_BAND);

  return (
    <div className={`result-line${mine ? ' mine' : ''}${shown ? '' : ' waiting'}`}>
      {/* The person on the left, their deck on the right, each with their own
          name, their own rating and their own swing. They are two ratings that
          move independently, and interleaving them was the confusion: a ring
          around someone's face was showing their deck's progress. */}
      <span className="rl-side pilot">
        <EloRing
          name={pilot?.name ?? '?'}
          avatar={pilot?.avatar}
          portion={portion}
          from={shown ? ringFrom : portion}
          wrapped={wrapped}
          // While placing, the ring counts games rather than rating, and a game
          // played is always a gain. Colouring it by the hidden delta's sign
          // would tell them which way the sealed number went.
          up={pilotRanked ? pilotDelta >= 0 : true}
        />
        <span className="rl-text">
          <span className="rl-name">
            {place !== undefined && <span className="rl-place">{ORDINAL[place] ?? `${place}th`}</span>}
            {pilot?.name}
          </span>
          <span className="rl-nums">
            <Rating elo={rolledPilot} played={pilotPlayed} ranked={pilotRanked} run={shown} />
            {/* Spec 2.5, taken the whole way: a rating in placement is sealed,
                and so is every swing that built it. The count's own "+1" says
                the game counted without saying what it was worth. */}
            {pilotRanked && (
              <span className={`delta ${pilotDelta >= 0 ? 'up' : 'down'}`}>
                {pilotDelta >= 0 ? '+' : ''}
                {Math.round(swingPilot)}
              </span>
            )}
          </span>
        </span>
      </span>

      <span className="rl-side deck">
        <span className="rl-text">
          <span className="rl-name">{deckLabel(deck)}</span>
          <span className="rl-nums">
            {deckRanked && (
              <span className={`delta ${deckDelta >= 0 ? 'up' : 'down'}`}>
                {deckDelta >= 0 ? '+' : ''}
                {Math.round(swingDeck)}
              </span>
            )}
            <Rating elo={rolledDeck} played={deckPlayed} ranked={deckRanked} run={shown} />
          </span>
        </span>
        <span
          className="rl-art"
          style={art ? { backgroundImage: `url(${art})` } : undefined}
          aria-hidden="true"
        />
      </span>
    </div>
  );
}

/**
 * A rating, or the count of games until there is one.
 *
 * The placement count gets the +1 that just earned it, floating clear of the
 * layout so a number that is going to sit there quietly for four more games
 * still marks the moment it moved.
 */
function Rating({
  elo,
  played,
  ranked,
  run,
}: {
  elo: number;
  played: number;
  ranked: boolean;
  run: boolean;
}) {
  return (
    <span className="rl-rating num">
      {ranked ? Math.round(elo) : `${played}/${PLACEMENT_GAMES}`}
      {!ranked && run && <span className="placement-plus">+1</span>}
    </span>
  );
}

/**
 * What the counter saw, once the ratings have finished moving.
 *
 * Only exists for games played through the live counter — a game typed in
 * afterwards has no life history to draw, and inventing one from the final
 * totals would be a chart of an assumption.
 *
 * Everything here is measured, not inferred. The log also records who had
 * priority when each change landed, which is the closest thing to "who dealt
 * this" that costs nobody a tap — but it is a guess (a board wipe on your own
 * turn kills you too), and a guess on a screen becomes a fact by Tuesday. It
 * stays in the data for season-length questions and off this page.
 */
function Breakdown({ match, at }: { match: Match; at: number }) {
  const { pilot } = useLookups();
  const log = match.damageLog ?? [];
  const endTurn = match.endTurn ?? 0;
  if (log.length === 0 || endTurn < 1) return null;

  const ids = match.entries.map((e) => e.pilotId);
  // One column per turn taken where the game recorded them, and one per round
  // for anything logged before the counter tracked that.
  const columns = columnsOf(match.turnLog, endTurn);
  const series = lifeSeries(ids, log, columns);

  const losses = log.filter((e) => e.delta < 0);
  const biggest = losses.reduce<(typeof losses)[number] | null>(
    (worst, e) => (worst === null || e.delta < worst.delta ? e : worst),
    null,
  );
  const totalLost = losses.reduce((sum, e) => sum + Math.abs(e.delta), 0);
  const selfLost = losses
    .filter((e) => e.from === null)
    .reduce((sum, e) => sum + Math.abs(e.delta), 0);
  const firstBlood = losses.reduce<number | null>(
    (first, e) => (first === null || e.turn < first ? e.turn : first),
    null,
  );

  /**
   * Four numbers, each one number.
   *
   * "24 on Valenwood" was a sentence in a slot sized for a figure, and it blew
   * its column out and squeezed the other three. Whose it was belongs
   * underneath, where a long name costs the layout nothing.
   */
  const stats: { label: string; value: string; sub?: string }[] = [
    { label: 'Turns', value: String(endTurn) },
    {
      label: 'Total damage',
      value: String(totalLost),
      // Worth surfacing on the summary too: a table that paid a fifth of its
      // life to its own lands played a different game from one that did not.
      sub: selfLost > 0 ? `${selfLost} self-inflicted` : undefined,
    },
  ];
  if (match.durationSeconds) {
    stats.push({
      label: 'Real time',
      value: clock(match.durationSeconds),
      sub: `${Math.round(match.durationSeconds / 60 / endTurn)}m a turn`,
    });
  }
  if (biggest) {
    stats.push({
      label: 'Biggest hit',
      value: String(Math.abs(biggest.delta)),
      // Whoever had priority when it landed, which is not quite the same as
      // whoever dealt it — life leaves a player on somebody's turn and that
      // somebody is usually but not always the reason. With no turn to credit
      // it to, it was their own.
      sub: biggest.from ? `by ${pilot(biggest.from)?.name ?? '?'}` : 'own turn',
    });
  }
  if (firstBlood !== null) stats.push({ label: 'First blood', value: `${firstBlood}T` });

  return (
    <div className="breakdown" style={{ animationDelay: `${at / 1000}s` }}>
      <div className="breakdown-stats">
        {stats.map((s) => (
          <div className="breakdown-stat" key={s.label}>
            <span className="breakdown-stat-value">{s.value}</span>
            <span className="breakdown-stat-label">{s.label}</span>
            {s.sub && <span className="breakdown-stat-sub">{s.sub}</span>}
          </div>
        ))}
      </div>

      <LifeTimeline ids={ids} series={series} columns={columns} />
    </div>
  );
}

/**
 * Every measurement the counter took, as stats down the side and players across
 * the top.
 *
 * That way round because there are two to four players and a dozen and a half
 * stats: a column per player stays readable on a phone, where a column per stat
 * would need eighteen of them and horizontal scrolling to read one row.
 *
 * Everything here is arithmetic on what was recorded. Where a number rests on
 * an inference it says so in its own label rather than in a footnote — "on
 * their turn" is not "dealt", and the distinction is the difference between a
 * measurement and a story about one.
 */
function PlayerTable({ match }: { match: Match }) {
  const { pilot } = useLookups();
  const log = match.damageLog ?? [];
  const endTurn = match.endTurn ?? 0;
  const ids = match.entries.map((e) => e.pilotId);

  /*
   * One column per turn *taken*, not per round.
   *
   * Worth naming, because everything below has to convert back. A nine-round
   * three-player game is twenty-five columns, and a life series indexed by
   * column read as though it were indexed by round is how this table came to
   * say somebody survived twenty-five turns of a game that lasted nine — and
   * then divided their life lost by that number, and counted "turns still on
   * 40" across the first nine columns, which is the first three rounds.
   */
  const columns = columnsOf(match.turnLog, endTurn);

  /**
   * Every row whose label says "turns" reads rounds off this.
   *
   * The conversion is in `shared` rather than here, where it can be tested —
   * it was wrong for a season without looking wrong, which is the argument for
   * both the move and the tests that came with it.
   */
  const survival = survivalOf(ids, log, columns, endTurn);

  const rows = ids.map((id) => {
    const taken = log.filter((e) => e.to === id && e.delta < 0);
    const dealt = log.filter((e) => e.from === id && e.delta < 0);
    /*
     * Life that left them on their own turn, credited to nobody.
     *
     * Fetchlands, painlands, Ad Nauseam, your own wrath — the game asks players
     * to pay life constantly and none of it shows up anywhere else. It is a
     * subset of damage taken rather than a separate total, because it is the
     * same life; this row only says how much of it they did to themselves.
     */
    const selfInflicted = taken
      .filter((e) => e.from === null)
      .reduce((sum, e) => sum + Math.abs(e.delta), 0);
    const takenTotal = taken.reduce((sum, e) => sum + Math.abs(e.delta), 0);
    const dealtTotal = dealt.reduce((sum, e) => sum + Math.abs(e.delta), 0);
    /** Rounds — when they went out, and how it felt getting there. */
    const { outTurn, alive, inDanger, untouched, left } = survival.get(id)!;

    const cmdTaken = Object.values(match.commanderDamage?.[id] ?? {});
    // Both of a partner pair count, and the rule is per commander, so the
    // number worth showing is the bigger of the two rather than their sum.
    const cmdDealt = ids
      .filter((other) => other !== id)
      .map((other) =>
        Math.max(
          match.commanderDamage?.[other]?.[id] ?? 0,
          match.commanderDamage?.[other]?.[commanderKey(id, true)] ?? 0,
        ),
      );

    return {
      id,
      place: match.standing?.[id],
      // The end of the line, not the round number as an index into it.
      left: match.lifeRemaining?.[id] ?? left,
      outTurn,
      takenTotal,
      selfInflicted,
      worstTaken: taken.reduce((low, e) => Math.min(low, e.delta), 0),
      bestDealt: dealt.reduce((low, e) => Math.min(low, e.delta), 0),
      gained: log
        .filter((e) => e.to === id && e.delta > 0)
        .reduce((sum, e) => sum + e.delta, 0),
      dealtTotal,
      takenPerTurn: alive > 0 ? takenTotal / alive : 0,
      dealtPerTurn: alive > 0 ? dealtTotal / alive : 0,
      firstHit: taken.reduce<number | null>(
        (first, e) => (first === null || e.turn < first ? e.turn : first),
        null,
      ),
      inDanger,
      untouched,
      poison: match.poison?.[id] ?? 0,
      poisonDealt: (match.poisonLog ?? [])
        .filter((e) => e.from === id && e.delta > 0)
        .reduce((sum, e) => sum + e.delta, 0),
      cmdTaken: cmdTaken.length ? Math.max(...cmdTaken) : 0,
      cmdDealt: cmdDealt.length ? Math.max(...cmdDealt) : 0,
      odds: match.predicted?.[id],
      onThePlay: match.onThePlay === id,
    };
  });

  /** Best in a column is worth marking; for some of them best is lowest. */
  const best = (pick: (r: (typeof rows)[number]) => number, low = false) => {
    const values = rows.map(pick).filter((v) => v > 0);
    if (values.length === 0) return null;
    return low ? Math.min(...values) : Math.max(...values);
  };

  /*
   * Labels say the whole thing, and the two that rest on an inference carry a
   * mark to the note that says so.
   *
   * Marks rather than hover: a phone has no hover, and the earlier question
   * marks explained nothing to anyone who did not already know to try. An
   * asterisk is a promise that there is a sentence about this below, which is
   * the one thing a reader can act on.
   */
  type Line = {
    label: string;
    mark?: '*' | '†';
    cell: (r: (typeof rows)[number]) => string;
    top?: number | null;
    pick?: (r: (typeof rows)[number]) => number;
  };

  // No deck row: commander names are long enough to set the width of every
  // column in the table, and they are already on the line above this view.
  const lines: Line[] = [
    { label: 'Finished', cell: (r) => (r.place ? (ORDINAL[r.place] ?? `${r.place}th`) : '—') },
    { label: 'Life at the end', cell: (r) => (r.outTurn ? `out ${r.outTurn}T` : String(r.left)) },
    { label: 'Turns survived', cell: (r) => String(r.outTurn ?? endTurn) },
    { label: 'Took the first turn', cell: (r) => (r.onThePlay ? 'yes' : 'no') },
    {
      label: 'Life lost, all game',
      cell: (r) => String(r.takenTotal || '—'),
      pick: (r) => r.takenTotal,
      top: best((r) => r.takenTotal),
    },
    {
      label: 'Life they cost themselves',
      mark: '†',
      cell: (r) => String(r.selfInflicted || '—'),
      pick: (r) => r.selfInflicted,
      top: best((r) => r.selfInflicted),
    },
    {
      label: 'Biggest hit they took',
      cell: (r) => String(Math.abs(r.worstTaken) || '—'),
      pick: (r) => Math.abs(r.worstTaken),
      top: best((r) => Math.abs(r.worstTaken)),
    },
    { label: 'Life lost per turn', cell: (r) => (r.takenTotal ? r.takenPerTurn.toFixed(1) : '—') },
    { label: 'First hit landed on turn', cell: (r) => (r.firstHit ? String(r.firstHit) : '—') },
    { label: 'Turns still on 40', cell: (r) => String(r.untouched || '—') },
    { label: 'Turns as the lowest life', cell: (r) => String(r.inDanger || '—') },
    { label: 'Life gained back', cell: (r) => String(r.gained || '—') },
    {
      label: 'Damage dealt',
      mark: '*',
      cell: (r) => String(r.dealtTotal || '—'),
      pick: (r) => r.dealtTotal,
      top: best((r) => r.dealtTotal),
    },
    {
      label: 'Biggest hit they dealt',
      mark: '*',
      cell: (r) => String(Math.abs(r.bestDealt) || '—'),
      pick: (r) => Math.abs(r.bestDealt),
      top: best((r) => Math.abs(r.bestDealt)),
    },
    {
      label: 'Damage dealt per turn',
      mark: '*',
      cell: (r) => (r.dealtTotal ? r.dealtPerTurn.toFixed(1) : '—'),
    },
    { label: 'Worst commander hit them', cell: (r) => String(r.cmdTaken || '—') },
    { label: 'Their commander dealt', cell: (r) => String(r.cmdDealt || '—') },
    { label: 'Poison on them', cell: (r) => String(r.poison || '—') },
    { label: 'Poison they gave out', mark: '*', cell: (r) => String(r.poisonDealt || '—') },
    {
      label: 'Win chance before it started',
      cell: (r) => (r.odds === undefined ? '—' : `${Math.round(r.odds * 100)}%`),
    },
  ];

  // Finishing order across the top, so the columns read like the result did.
  const ordered = match.standing
    ? [...rows].sort((a, b) => (a.place ?? 99) - (b.place ?? 99))
    : rows;

  return (
    <div className="stat-table-wrap">
      <table className="stat-table">
        <thead>
          <tr>
            <th />
            {ordered.map((r) => (
              <th key={r.id}>
                <span className="stat-head">
                  <Avatar name={pilot(r.id)?.name ?? '?'} src={pilot(r.id)?.avatar} size={26} />
                  <span className="stat-head-name">{pilot(r.id)?.name}</span>
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.label}>
              <th scope="row">
                {line.label}
                {line.mark && <span className="stat-mark">{line.mark}</span>}
              </th>
              {ordered.map((r) => {
                const value = line.cell(r);
                const isTop =
                  line.top != null && line.pick != null && line.pick(r) === line.top && r.takenTotal + r.dealtTotal > 0;
                return (
                  <td key={r.id} className={`num${isTop ? ' top' : ''}`}>
                    {value}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <dl className="stat-footnote">
        <dt>*</dt>
        <dd>
          Worked out from whose turn it was, not from who swung. Life or poison that left
          another player while this one had priority counts to them — right nearly always,
          and wrong when somebody else’s drain goes off on your turn.
        </dd>
        <dt>†</dt>
        <dd>
          Life they lost on their own turn with nobody to blame: fetchlands, painlands,
          their own board wipe. Part of the life lost above rather than on top of it.
        </dd>
      </dl>
    </div>
  );
}
